import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logAuthEvent } from "@/lib/authAudit";

type AuthContextType = {
  session: any;
  roles: string[];
  tier: string;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  roles: [],
  tier: "tier_1",
  loading: true,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<any>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [tier, setTier] = useState<string>("tier_1");
  const [loading, setLoading] = useState(true);

  // initial session + listener
  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session ?? null);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (import.meta.env.DEV) console.log("Auth state change:", event, newSession?.user?.email);
      const prevSession = session;
      setSession(newSession ?? null);
      if (!newSession) {
        setRoles([]);
        setTier("tier_1");
        // Log logout when session disappears (user signed out)
        if (prevSession?.user) {
          logAuthEvent(prevSession.user.id, prevSession.user.email ?? "unknown", "logout");
        }
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // load roles + tier when session changes - only if user exists
  useEffect(() => {
    let cancelled = false;

    const loadRolesAndTier = async () => {
      const uid = session?.user?.id;
      
      // If not logged in, do NOT query
      if (!uid) {
        setRoles([]);
        setTier("tier_1");
        return;
      }

      let userRoles: string[] = [];

      // Use RPC only (bypasses RLS via SECURITY DEFINER)
      const { data: rpcRows, error: rpcErr } = await supabase.rpc("current_user_roles");
      if (rpcErr) {
        console.warn("[Auth] current_user_roles RPC failed:", rpcErr);
      } else {
        userRoles = Array.isArray(rpcRows) ? rpcRows.map((r: any) => typeof r === "string" ? r : r.role).filter(Boolean) : [];
      }

      // Also get sub_org_memberships for PM role via RPC
      const { data: somRpcRows, error: somRpcErr } = await supabase.rpc("current_user_sub_org_roles");
      if (somRpcErr) {
        console.warn("[Auth] current_user_sub_org_roles RPC failed:", somRpcErr);
      } else {
        const somRoles = Array.isArray(somRpcRows) ? somRpcRows.map((r: any) => typeof r === "string" ? r : r.role).filter(Boolean) : [];
        userRoles = [...userRoles, ...somRoles];
      }

      // Normalize super_admin to admin
      const normalizedRoles = userRoles.map(r => r === "super_admin" ? "admin" : r);
      const uniqueRoles = Array.from(new Set(normalizedRoles));

      // Fetch tier via SECURITY DEFINER RPC (bypasses RLS safely).
      // profiles.tier is the SOLE source of truth — roles never override it.
      // RPC returns 'tier_1' as a safe default when no row / null tier.
      const { data: rpcTier, error: tierErr } = await supabase.rpc("get_my_tier");
      if (tierErr) {
        console.warn("[Auth] get_my_tier RPC failed:", tierErr);
      }

      const validTiers = ["tier_1", "tier_2", "tier_3"];
      const userTier: string =
        typeof rpcTier === "string" && validTiers.includes(rpcTier)
          ? rpcTier
          : "tier_1";

      console.log("[Auth] tier resolution", {
        uid,
        rpcTier,
        tierErr: tierErr ? String((tierErr as any).message ?? tierErr) : null,
        roles: uniqueRoles,
        resolvedTier: userTier,
      });

      if (cancelled) return;
      setRoles(uniqueRoles);
      setTier(userTier);
    };

    loadRolesAndTier();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  return (
    <AuthContext.Provider value={{ session, roles, tier, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);